import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  GenerateSignalsResult,
  GeneratedSignal,
  SignalHistoryRow,
  SignalHistoryStatus,
} from "../api/dataSource";
import { SignalTradeTicketModal } from "../components/SignalTradeTicketModal";
import { SymbolLabel } from "../components/SymbolLabel";
import { assetClassOf } from "../lib/assetClass";
import { fmtPx } from "../lib/format";
import type {
  EventRow,
  Pending,
  PositionReassessment,
  TradePreview,
} from "../types";

type Props = {
  pending: Pending[];
  positionReassessments?: PositionReassessment[];
  recentEvents: EventRow[];
  onOpenApproval: (approvalId: string) => void;
  onGenerateSignals: () => Promise<GenerateSignalsResult>;
  listSignalHistory: (limit?: number) => Promise<SignalHistoryRow[]>;
  onRepriceSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<string>;
  onPreviewSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<{
    preview: TradePreview;
    actionable: boolean;
    issues: string[];
  }>;
};

type BriefingStatus = SignalHistoryStatus | "reprice";

type BriefingRow = {
  key: string;
  signal: GeneratedSignal;
  generatedAt: string | null;
  approvalStatus: BriefingStatus;
  approvalId: string | null;
  rationale: string;
};

const scoreClass = (score: number): string => {
  if (score >= 70) return "pos";
  if (score >= 55) return "";
  return "flat";
};

const planForSignal = (signal: GeneratedSignal): string => {
  const target = signal.targets[0];
  return [
    fmtPx(signal.entry),
    " stop ",
    fmtPx(signal.stop),
    target === undefined ? "" : ` -> ${fmtPx(target)}`,
  ].join("");
};

const researchLabel = (signal: GeneratedSignal): string | null => {
  const r = signal.researchEnrichment;
  if (!r) return null;
  if (r.status === "failed") return "research unavailable";
  if (r.status !== "ready") return null;
  const score =
    r.compositeScore === undefined || Number.isNaN(r.compositeScore)
      ? ""
      : ` ${r.compositeScore}`;
  const action = r.approvalAction ?? "neutral";
  return `research ${action}${score}`;
};

const signalKey = (signal: GeneratedSignal): string =>
  signal.signalId ??
  `${signal.symbol}-${signal.strategyId}-${signal.barDate}-${signal.direction}`;

const isStopBreachedRepriceError = (message: string): boolean =>
  message.includes("must be above stop") ||
  message.includes("must be below stop");

const statusLabel: Record<BriefingStatus, string> = {
  skipped: "not approved",
  pending: "pending",
  expired: "expired",
  approved: "approved",
  placed: "placed",
  reprice: "needs reprice",
};

const statusKind = (status: BriefingStatus): string => {
  if (status === "placed" || status === "approved") return "ok";
  if (status === "pending") return "info";
  if (status === "expired" || status === "reprice") return "warn";
  return "idle";
};

const fmtGeneratedAt = (iso: string | null): string => {
  if (iso === null) return "queued";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pendingAsSignal = (p: Pending): GeneratedSignal => ({
  signalId: null,
  symbol: p.sym,
  companyName: p.companyName,
  strategyId: p.strategy,
  // Phase 9-UI fix: was hardcoded "equity", which mis-sized any
  // non-equity ticket opened from Briefing (crypto got whole-share
  // flooring; forex would have too). Classify from the row.
  assetClass: assetClassOf(p),
  barDate: "",
  direction: p.side,
  entry: p.entry,
  stop: p.stop,
  targets: p.targets,
  score: p.det,
  factors: p.cited ? [p.cited] : [],
  rrRatio: p.targets[0]
    ? p.side === "short"
      ? (p.entry - p.targets[0]) / (p.stop - p.entry)
      : (p.targets[0] - p.entry) / (p.entry - p.stop)
    : 0,
  aiConfidence: p.scoreKnown === false ? null : p.ai,
  aiRationale: p.reason,
  aiRiskFlags: p.aiRiskFlags,
  aiModelId: null,
  researchEnrichment: undefined,
  edgeScore: null,
  regimeFit: null,
  portfolioFit: null,
  approvalGateReason: null,
  signalRole: "entry",
});

const pendingAsRow = (p: Pending): BriefingRow => {
  const signal = pendingAsSignal(p);
  return {
    key: `pending-${p.approvalId}`,
    signal,
    generatedAt: null,
    approvalStatus: "pending",
    approvalId: p.approvalId,
    rationale: p.reason,
  };
};

const historyAsSignal = (row: SignalHistoryRow): GeneratedSignal => ({
  signalId: row.signalId,
  symbol: row.symbol,
  companyName: row.companyName,
  strategyId: row.strategyId,
  assetClass: row.assetClass ?? "equity",
  barDate: row.barDate,
  direction: row.direction,
  entry: row.entry,
  stop: row.stop,
  targets: row.targets,
  score: row.score,
  factors: row.rationale ? [row.rationale] : [],
  rrRatio: row.rrRatio,
  aiConfidence: null,
  aiRationale: row.rationale,
  aiRiskFlags: [],
  aiModelId: row.aiModelId,
  researchEnrichment: row.researchEnrichment,
  edgeScore: row.edgeScore,
  regimeFit: row.regimeFit,
  portfolioFit: row.portfolioFit,
  approvalGateReason: row.approvalGateReason,
  signalRole: row.signalRole,
});

const historyAsRow = (row: SignalHistoryRow): BriefingRow => ({
  key: `history-${row.signalId}`,
  signal: historyAsSignal(row),
  generatedAt: row.generatedAt,
  approvalStatus: row.approvalStatus,
  approvalId: row.approvalId,
  rationale: row.rationale,
});

const generatedAsRow = (signal: GeneratedSignal): BriefingRow => ({
  key: `run-${signalKey(signal)}`,
  signal,
  generatedAt: null,
  approvalStatus: "reprice",
  approvalId: null,
  rationale: signal.aiRationale ?? signal.factors.join(", "),
});

const briefingEventMatches = (e: EventRow): boolean => {
  const type = e.type.toLowerCase();
  if (
    type.startsWith("signal_") ||
    type.startsWith("signals_") ||
    type.startsWith("approval_") ||
    type.startsWith("approvals_") ||
    type.startsWith("auto_signal_") ||
    type.startsWith("position_reassessment")
  ) {
    return true;
  }
  return [
    "data_freshness_stale",
    "system_health_circuit_breaker_tripped",
    "pf_gap_intervention",
    "basket_warn",
    "session_open",
    "regime_classified",
    "intraday_signal_tick_complete",
    "crypto_signal_push_sent",
    "crypto_signal_push_failed",
  ].includes(type);
};

export const BriefingScreen = ({
  pending,
  positionReassessments = [],
  recentEvents,
  onOpenApproval,
  onGenerateSignals,
  listSignalHistory,
  onRepriceSignal,
  onPreviewSignal,
}: Props) => {
  const [running, setRunning] = useState(false);
  const [repricingKey, setRepricingKey] = useState<string | null>(null);
  const [ticketRow, setTicketRow] = useState<BriefingRow | null>(null);
  const [result, setResult] = useState<GenerateSignalsResult | null>(null);
  const [historyRows, setHistoryRows] = useState<SignalHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [strategyFilter, setStrategyFilter] = useState("all");
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      setHistoryRows(await listSignalHistory(500));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load signal history");
    } finally {
      setHistoryLoading(false);
    }
  }, [listSignalHistory]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const rows = useMemo(() => {
    const next = historyRows.map(historyAsRow);
    const seenSignals = new Set(next.map((row) => signalKey(row.signal)));
    const seenApprovals = new Set(
      next.flatMap((row) => (row.approvalId ? [row.approvalId] : [])),
    );

    if (result !== null) {
      for (const signal of result.signals) {
        const key = signalKey(signal);
        if (!seenSignals.has(key)) {
          next.unshift(generatedAsRow(signal));
          seenSignals.add(key);
        }
      }
    }

    for (const p of pending) {
      if (seenApprovals.has(p.approvalId)) continue;
      const row = pendingAsRow(p);
      if (!seenSignals.has(signalKey(row.signal))) {
        next.unshift(row);
        seenSignals.add(signalKey(row.signal));
      }
    }

    return next;
  }, [historyRows, pending, result]);

  const strategyOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.signal.strategyId))).sort(),
    [rows],
  );

  const visibleRows = useMemo(
    () =>
      strategyFilter === "all"
        ? rows
        : rows.filter((row) => row.signal.strategyId === strategyFilter),
    [rows, strategyFilter],
  );

  const signalEvents = useMemo(
    () => recentEvents.filter(briefingEventMatches),
    [recentEvents],
  );

  const runBriefing = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      const next = await onGenerateSignals();
      setResult(next);
      await loadHistory();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Briefing scan failed");
    } finally {
      setRunning(false);
    }
  }, [loadHistory, onGenerateSignals]);

  const repriceSignal = useCallback(
    async (row: BriefingRow, riskDollars: string) => {
      setRepricingKey(row.key);
      setError(null);
      try {
        const approvalId = await onRepriceSignal(row.signal, riskDollars);
        setTicketRow(null);
        await loadHistory();
        onOpenApproval(approvalId);
        return approvalId;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Reprice failed";
        if (isStopBreachedRepriceError(message)) {
          setResult((curr) =>
            curr
              ? {
                  ...curr,
                  approvalsSkipped: Math.max(0, curr.approvalsSkipped - 1),
                  signals: curr.signals.filter(
                    (s) => signalKey(s) !== signalKey(row.signal),
                  ),
                }
              : curr,
          );
        }
        setError(message);
        throw e;
      } finally {
        setRepricingKey(null);
      }
    },
    [loadHistory, onOpenApproval, onRepriceSignal],
  );

  return (
    <div data-screen-label="02 Briefing">
      <div className="tp-page-head">
        <h1>Briefing</h1>
        <div className="meta">
          {result
            ? `${result.asOf} · ${result.universeSize} symbols scanned`
            : historyLoading
              ? "loading generated signals"
              : `${visibleRows.length} of ${rows.length} generated signals`}
        </div>
      </div>

      <div className="tp-actionbar">
        <select
          className="tp-input"
          value={strategyFilter}
          aria-label="Strategy filter"
          onChange={(e) => setStrategyFilter(e.target.value)}
        >
          <option value="all">all strategies</option>
          {strategyOptions.map((strategy) => (
            <option key={strategy} value={strategy}>
              {strategy}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="tp-btn tp-btn-secondary"
          disabled={running}
          onClick={() => void runBriefing()}
        >
          {running ? "Running..." : "Run briefing"}
        </button>
        {result && (
          <span className="tp-inline-status">
            {result.signalsGenerated} signal
            {result.signalsGenerated === 1 ? "" : "s"} ·{" "}
            {result.longSignals} long · {result.shortSignals} short ·{" "}
            {result.hedgeWatchSignals} hedge · min{" "}
            {result.autoApprovalConfidenceThreshold} ·{" "}
            {result.approvalsCreated} approval
            {result.approvalsCreated === 1 ? "" : "s"} queued ·{" "}
            {result.regime}
          </span>
        )}
        {error && <span className="tp-inline-status bad">{error}</span>}
      </div>

      {positionReassessments.length > 0 && (
        <div className="tp-tile" style={{ marginBottom: 14 }}>
          <div className="tp-tile-head">
            <span className="label">
              Position reviews ({positionReassessments.length})
            </span>
            <span className="tp-muted">source: open-position reassessment</span>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            <table className="tp-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Decision</th>
                  <th>Action</th>
                  <th>Severity</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {positionReassessments.slice(0, 6).map((row) => (
                  <tr key={row.reassessmentId}>
                    <td className="ticker">
                      <SymbolLabel
                        symbol={row.symbol}
                        companyName={row.companyName}
                      />
                    </td>
                    <td>{row.recommendation.replace(/_/g, " ")}</td>
                    <td>{row.proposedAction.replace(/_/g, " ")}</td>
                    <td>{row.severity}</td>
                    <td className="num">
                      {new Date(row.expiresAt).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="tp-tile" style={{ marginBottom: 14 }}>
        <div className="tp-tile-head">
          <span className="label">Candidates ({visibleRows.length})</span>
          <span className="tp-muted">source: generated-signal history</span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          <div className="tp-table-scroll">
            <table className="tp-table tp-briefing-table">
              <thead>
                <tr>
                  <th>Generated</th>
                  <th>Symbol</th>
                  <th>Strategy</th>
                  <th>Side</th>
                  <th className="center">Score</th>
                  <th>Plan</th>
                  <th>AI review</th>
                  <th>Status</th>
                  <th className="right">Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="tp-empty-cell">
                      {historyLoading
                        ? "Loading generated signals..."
                        : "No briefing candidates yet."}
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row) => {
                    const signal = row.signal;
                    const pendingMatch = pending.find(
                      (p) =>
                        p.approvalId === row.approvalId ||
                        (p.sym === signal.symbol &&
                          p.strategy === signal.strategyId),
                    );
                    const approvalId = row.approvalId ?? pendingMatch?.approvalId;
                    const canReview =
                      row.approvalStatus === "pending" &&
                      approvalId !== undefined &&
                      approvalId !== null;
                    const canReprice =
                      row.approvalStatus === "skipped" ||
                      row.approvalStatus === "expired" ||
                      row.approvalStatus === "reprice";
                    return (
                      <tr key={row.key}>
                        <td className="num">{fmtGeneratedAt(row.generatedAt)}</td>
                        <td className="ticker">
                          <SymbolLabel
                            symbol={signal.symbol}
                            companyName={signal.companyName}
                          />
                        </td>
                        <td className="num">{signal.strategyId}</td>
                        <td className="num">{signal.direction}</td>
                        <td className={`center num ${scoreClass(signal.score)}`}>
                          {signal.score}
                        </td>
                        <td className="num">{planForSignal(signal)}</td>
                        <td>
                          <span className="tp-ai-cell">
                            {signal.aiConfidence === null
                              ? "indicator-only"
                              : `${signal.aiConfidence} AI`}
                            {researchLabel(signal) !== null && (
                              <span className="tp-risk-flags">
                                {researchLabel(signal)}
                              </span>
                            )}
                            {signal.aiRiskFlags.length > 0 && (
                              <span className="tp-risk-flags">
                                {signal.aiRiskFlags.join(", ")}
                              </span>
                            )}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`tp-status tp-status-compact ${statusKind(
                              row.approvalStatus,
                            )}`}
                            title={row.rationale}
                          >
                            <span className="dot" />
                            {statusLabel[row.approvalStatus]}
                          </span>
                        </td>
                        <td className="right">
                          {canReview ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => onOpenApproval(approvalId)}
                            >
                              Review
                            </button>
                          ) : canReprice ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              disabled={repricingKey === row.key}
                              onClick={() => setTicketRow(row)}
                            >
                              {repricingKey === row.key
                                ? "Queueing..."
                                : "Review"}
                            </button>
                          ) : (
                            <span className="num flat">closed</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {ticketRow && (
        <SignalTradeTicketModal
          signal={ticketRow.signal}
          onClose={() => setTicketRow(null)}
          onPreview={onPreviewSignal}
          onConfirm={(riskDollars) => repriceSignal(ticketRow, riskDollars)}
        />
      )}

      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Briefing events</span>
        </div>
        <div className="tp-tile-body">
          {signalEvents.length === 0 ? (
            <div className="tp-empty-state">No signal events recorded.</div>
          ) : (
            signalEvents.slice(0, 8).map((e, i) => (
              <div key={`${e.time}-${i}`} className="tp-evt">
                <span className="time">{e.time}</span>
                <span className="type">{e.type}</span>
                <span className="summary">{e.summary}</span>
                <span className="actor">{e.actor}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
